import { PaginationState } from "@tanstack/react-table";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import classNames from "classnames";
import DocumentsService from "../../../common/api/documentsApi";
import { createDocument } from "../../../common/api/notificationApi";
import AlertBox from "../../../shared/Ui/AlertBox/AlertBox";
import CheckBox from "../../../shared/Ui/CheckBox/CheckBox";
import { EmptyListPlaceholderContent } from "../../../shared/Ui/EmptyPageBlock";
import Loader from "../../../shared/Ui/Loader/Loader";
import ModalButton from "../../../shared/Ui/Buttons/ModalButtons";
import ModalComponent from "../../../shared/Ui/ModalComponent/ModalWindow";
import PageNotRole from "../../../shared/Ui/PageNotFound/PageNotRole";
import StatusBadge from "../../../shared/Ui/StatusBadge/StatusBadge";
import Stepper from "../../../shared/components/Stepper/Stepper";
import Pagination from "../../../shared/components/Table/Pagination";
import { ITable } from "../../../shared/components/Table/types";
import { getDescription, getStatusType } from "../../../shared/documentUtils/dictionary";
import { useAppDispatch, useAppSelector } from "../../../shared/hook";
import { clearBlocker, setBlocker } from "../../../shared/slice/blockerSlice";
import { submitXmlProduct } from "../../../shared/signature/documentsSignature";
import { AGGREGATION_DOC_TYPE } from "./DocumentAggregation";
import style from "./DocumentAggregation.module.css";
import { MarkScan } from "../Upload/MarkScan/MarkScan";
import { AddByOrderNumber } from "../Upload/AddByOrderNumber/AddByOrderNumber";
import { DocumentsSearch } from "../Upload/DocumentsSearch/DocumentsSearch";
import { DocumentUpload } from "../Upload/DocumentUpload/DocumentUpload";
import CheckResult from "../Upload/CheckResults/CheckResult";
import Cookies from "js-cookie";  

type OpType = "AGGREGATION" | "DEAGGREGATION";
type ScanFor = "parent" | "children" | "deaggregation";

const normalizeMarkCode = (value: string) =>
  String(value || "")
    .replace(/[\x00-\x1F\x7F]/g, "")
    .trim();

const markCodesMatch = (a: string, b: string) => {
  const left = normalizeMarkCode(a);
  const right = normalizeMarkCode(b);
  if (!left || !right) return false;
  if (left === right) return true;
  const leftShort = left.slice(0, 21);
  const rightShort = right.slice(0, 21);
  return (
    left.startsWith(rightShort) ||
    right.startsWith(leftShort) ||
    leftShort === rightShort
  );
};

/** list: sourceAggregationDocumentIds[]; add: по одному sourceAggregationDocumentId на item */
const getSourceAggregationDocumentIds = (row: any): number[] => {
  if (Array.isArray(row?.sourceAggregationDocumentIds)) {
    return row.sourceAggregationDocumentIds
      .map((x: any) => Number(x))
      .filter((x: number) => Number.isFinite(x));
  }
  if (row?.sourceAggregationDocumentId != null) {
    const id = Number(row.sourceAggregationDocumentId);
    return Number.isFinite(id) ? [id] : [];
  }
  return [];
};

const toDeaggAddItems = (
  markId: number,
  row: any,
): { markId: number; sourceAggregationDocumentId: number }[] =>
  getSourceAggregationDocumentIds(row).map((sourceAggregationDocumentId) => ({
    markId,
    sourceAggregationDocumentId,
  }));

/** Агрегационный код — из GET /documents/product/list (марки, добавленные через add-marks*) */
const extractParentFromProducts = (
  products: any[],
): { id: number; code: string } | null => {
  if (!products?.length) return null;
  const row = products[0];
  const id = row?.markId ?? row?.id;
  if (id == null || id === "") return null;
  const code = row?.markCode || row?.code || String(id);
  return { id: Number(id), code: String(code) };
};

const formatKmTypeLabel = (kmType: string | undefined | null, t: (key: string) => string) => {
  if (kmType === "GROUP") return t("new_order.km_type_group");
  if (kmType === "CONSUMER") return t("new_order.km_type_consumer");
  if (kmType === "TRANSPORT") return t("new_order.km_type_transport");
  return kmType || "-";
};

function DocumentAggregationOrders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { aggregationDocumentId: paramDocId } = useParams();
  const roleMatrix = useAppSelector((state) => state.roleMatrix);
  const pageSize = useAppSelector((state) => state.userSettings.pageSize);

  const isNewRoute = !paramDocId || paramDocId === "new";
  const [createdDocId, setCreatedDocId] = useState<string | null>(null);
  const docId = createdDocId || (!isNewRoute ? paramDocId : undefined);

  const [role, setRole] = useState<any>(null);
  const [opType, setOpType] = useState<OpType>("AGGREGATION");
  const [loading, setLoading] = useState(!isNewRoute);
  const [status, setStatus] = useState<any>("new");
  const [statusType, setStatusType] = useState({
    type: "new",
    label: t("documentStatus.new"),
  });
  const [initialValues, setInitialValues] = useState<any>({});
  const [tableData, setTableData] = useState<ITable>();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize || 10,
  });
  const [parentMark, setParentMark] = useState<{ id: number; code: string } | null>(null);
  const [deleteMarks, setDeleteMarks] = useState<number[]>([]);
  const [alertOpen, setAlertOpen] = useState<any>(null);
  const [errorModal, setErrorModal] = useState<any>(null);
  const [isCheckOpen, setIsCheckOpen] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<any>(null);
  const [parentModalOpen, setParentModalOpen] = useState(false);
  const [childrenModalOpen, setChildrenModalOpen] = useState(false);
  const [isMarkScanModal, setIsMarkScanModal] = useState(false);
  const [scanFor, setScanFor] = useState<ScanFor>("children");
  const scanForRef = useRef<ScanFor>("children");
  const [toggleOnLoad, setToggleOnLoad] = useState(false);
  const [deaggModalOpen, setDeaggModalOpen] = useState(false);
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);

  const isDraft = status === "new" || status === "DRAFT";
  const canEdit = isDraft && (role?.roleDraft?.edit ?? true);
  /** Тип операции выбирается только до создания черновика */
  const canChangeOpType = status === "new" && canEdit;

  useEffect(() => {
    const roleObj = () => {
      let roleWithSpecificAction = roleMatrix?.roleMatrix?.find(
        (item: any) => item.id === 12,
      );
      const roleDraft = roleMatrix?.roleMatrix?.find((item: any) => item.id === 6);
      if (!roleWithSpecificAction?.view) {
        roleWithSpecificAction = roleMatrix?.roleMatrix?.find(
          (item: any) => item.id === 5,
        );
      }
      return {
        roleDoc: roleWithSpecificAction?.specificAction?.documents?.[0] || false,
        roleDraft: roleDraft || false,
        roleAccepted: roleWithSpecificAction || false,
      };
    };
    setRole(roleObj());
  }, [roleMatrix]);

  useEffect(() => {
    onLoad();
  }, [paramDocId, createdDocId, pagination.pageIndex, pagination.pageSize, toggleOnLoad]);

  /** Черновик создаём только по Save или при добавлении кодов — не при входе на /new */
  const ensureDocument = async (): Promise<string | null> => {
    if (docId) return docId;
    try {
      const response = await createDocument({
        refDocType: AGGREGATION_DOC_TYPE,
        aggregationType: opType,
        note: null,
      });
      const id = response?.data?.document?.id;
      if (!id) {
        setAlertOpen({
          status: "error",
          message: t("documentNotification.document_save_error"),
        });
        return null;
      }
      const idStr = String(id);
      setCreatedDocId(idStr);
      setStatus("DRAFT");
      setStatusType(getStatusType("DRAFT", t));
      navigate(`/documents/aggregation/${idStr}/orders`, { replace: true });
      return idStr;
    } catch (e: any) {
      setAlertOpen({
        status: "error",
        message:
          e?.response?.data?.message ||
          t("documentNotification.document_save_error"),
      });
      return null;
    }
  };

  async function onLoad(overrideDocId?: string) {
    const effectiveId =
      overrideDocId ||
      createdDocId ||
      (!isNewRoute ? paramDocId : undefined);

    if (!effectiveId) {
      setLoading(false);
      setStatus("new");
      setStatusType({ type: "new", label: t("documentStatus.new") });
      setInitialValues({});
      setTableData(undefined);
      setParentMark(null);
      return;
    }
    setLoading(true);
    try {
      const docRes = await DocumentsService.getDocument(effectiveId);
      const data = docRes?.data || docRes || {};
      setInitialValues(data);
      setStatus(data.status);
      const st = getStatusType(data.status, t);
      setStatusType(st);

      const isDeagg = data.aggregationType === "DEAGGREGATION";
      if (isDeagg) {
        setOpType("DEAGGREGATION");
        setParentMark(null);
        // Разагрегация: марки документа — product/list (не deaggregation/list — это кандидаты)
        const marksRes = await DocumentsService.getDocMarksList(
          String(effectiveId),
          pagination.pageIndex + 1,
          pagination.pageSize,
        );
        const rows =
          marksRes?.products ||
          marksRes?.marks ||
          marksRes?.data?.products ||
          marksRes?.data?.marks ||
          [];
        setTableData({
          values: rows,
          currentPage: marksRes?.currentPage ?? marksRes?.data?.currentPage,
          itemsPerPage: marksRes?.itemsPerPage ?? marksRes?.data?.itemsPerPage,
          totalItems: marksRes?.totalItems ?? marksRes?.data?.totalItems,
          totalPages: marksRes?.totalPages ?? marksRes?.data?.totalPages,
        });
      } else {
        setOpType("AGGREGATION");
        // Parent — product/list; children — aggregation/children
        const [parentRes, childrenRes] = await Promise.all([
          DocumentsService.getDocMarksList(String(effectiveId), 1, 1, "ASC"),
          DocumentsService.getAggregationChildren(
            effectiveId,
            pagination.pageIndex + 1,
            pagination.pageSize,
          ),
        ]);
        setParentMark(
          extractParentFromProducts(
            parentRes?.products || parentRes?.data?.products || [],
          ),
        );
        setTableData({
          values: childrenRes?.marks || childrenRes?.data?.marks || [],
          currentPage: childrenRes?.currentPage,
          itemsPerPage: childrenRes?.itemsPerPage,
          totalItems: childrenRes?.totalItems,
          totalPages: childrenRes?.totalPages,
        });
      }
    } catch (e) {
      console.error(e);
      navigate("/404");
    } finally {
      setLoading(false);
    }
  }

  /** После add-marks* — подтянуть parent из product/list */
  const persistParent = (mark: { id: number; code: string }) => {
    setParentMark(mark);
    dispatch(setBlocker(true));
    onLoad();
  };

  const hasChildMarks = (tableData?.totalItems || 0) > 0;

  const clearParent = async () => {
    if (!parentMark?.id || !canEdit) return;
    if (hasChildMarks) {
      setErrorModal(t("document_aggregation.parent_delete_blocked"));
      return;
    }
    if (!docId) {
      setParentMark(null);
      return;
    }
    try {
      await DocumentsService.deleteMarks(docId, [String(parentMark.id)]);
      setParentMark(null);
      dispatch(setBlocker(true));
      onLoad();
    } catch (e: any) {
      setErrorModal(
        e?.response?.data?.message || t("documents_upload.error"),
      );
    }
  };

  /** Открыть выбор parent: create черновика (если new), затем модалка — без лишнего edit */
  const openParentPicker = async (via: "list" | "scan") => {
    const id = await ensureDocument();
    if (!id) return;
    if (via === "scan") {
      // MarkScan → add-marks-by-codes-v2 (без aggregationParentMarkId)
      scanForRef.current = "parent";
      setScanFor("parent");
      setIsMarkScanModal(true);
    } else {
      setParentModalOpen(true);
    }
  };

  const handleDeaggregationScanCodes = async (codes: string[]) => {
    const normalized = codes.map((c) => normalizeMarkCode(c)).filter(Boolean);
    if (!normalized.length) {
      throw new Error(t("documents_upload.error"));
    }
    const id = await ensureDocument();
    if (!id) {
      throw new Error(t("documentNotification.document_save_error"));
    }
    const productGroupId =
      Number(Cookies.get("activeGroup") || 0) || undefined;
    const res = await DocumentsService.getDeaggregationMarksList(1, 200, {
      productGroupId,
    });
    const marks = res?.marks || [];
    const selected: { markId: number; sourceAggregationDocumentId: number }[] =
      [];
    for (const code of normalized) {
      const found = marks.find((m: any) => markCodesMatch(m.code, code));
      if (found?.id != null) {
        selected.push(...toDeaggAddItems(found.id, found));
      }
    }
    if (!selected.length) {
      throw {
        response: {
          data: { message: t("documents_orders.no_data_title") },
        },
      };
    }
    await DocumentsService.addDeaggregationMarks(id, selected);
    await onLoad(String(id));
  };

  const openDeaggPicker = async (via: "list" | "scan") => {
    const id = await ensureDocument();
    if (!id) return;
    if (via === "scan") {
      scanForRef.current = "deaggregation";
      setScanFor("deaggregation");
      setIsMarkScanModal(true);
    } else {
      setDeaggModalOpen(true);
    }
  };

  const handleSave = async () => {
    try {
      if (!docId) {
        const id = await ensureDocument();
        if (!id) return;
        dispatch(clearBlocker());
        setAlertOpen({
          status: "SUCCESS",
          message: t("documentNotification.document_saved_success"),
        });
        return;
      }
      await DocumentsService.editDocument({
        id: docId,
        refDocType: AGGREGATION_DOC_TYPE,
        aggregationType: opType,
      });
      dispatch(clearBlocker());
      setAlertOpen({
        status: "SUCCESS",
        message: t("documentNotification.document_edited_success"),
      });
      onLoad();
    } catch (e: any) {
      setAlertOpen({
        status: "error",
        message:
          e?.response?.data?.message ||
          t("documentNotification.document_edit_error"),
      });
    }
  };

  /** Тип операции только локально и только до создания документа */
  const handleOpTypeChange = (next: OpType) => {
    if (!canChangeOpType) return;
    setOpType(next);
  };

  const handleSign = async () => {
    const id = docId || (await ensureDocument());
    if (!id) return;
    if (opType === "AGGREGATION" && !parentMark?.id) {
      setErrorModal(t("document_aggregation.parent_required"));
      return;
    }
    if (!tableData?.totalItems) {
      setErrorModal(t("document_aggregation.marks_required"));
      return;
    }
    submitXmlProduct(
      id,
      navigate,
      setAlertOpen,
      setLoading,
      setErrorModal,
      setIsCheckOpen,
      t,
      "aggregation",
    );
  };

  const getDocument = async () => {
    try {
      if (!docId) return;
      setLoading(true);
      const fileBlob = await DocumentsService.downloadDocumentMarks(docId);
      const downloadUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `${initialValues?.docNumber}.xlsx`);
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    } catch (error) {
      setAlertOpen({
        message: error ? error : "Ошибка при скачивании файла:",
      });
      console.error("Ошибка при скачивании файла:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMarks = async () => {
    const idsToDelete = Array.isArray(isDeleteModalOpen)
      ? isDeleteModalOpen.filter((id: any) => id != null && id !== "")
      : [];
    if (!docId || !idsToDelete.length) return;
    try {
      if (opType === "AGGREGATION") {
        await DocumentsService.deleteAggregationChildren(docId, {
          markIds: idsToDelete,
          markIdsOrMarkCodes: true,
        });
      } else {
        await DocumentsService.deleteMarks(docId, idsToDelete.map(String));
      }
      setDeleteMarks((prev) =>
        prev.filter(
          (id) =>
            !idsToDelete.some(
              (delId: any) => String(delId) === String(id),
            ),
        ),
      );
      setIsDeleteModalOpen(null);
      onLoad();
    } catch (e: any) {
      setErrorModal(e?.response?.data?.message || t("documents_upload.error"));
    }
  };

  if (loading && !role) return <Loader />;
  if (role && !role?.roleAccepted?.view && !role?.roleDraft?.view) {
    return <PageNotRole />;
  }

  return (
    <div className="max-w-[1585px] w-full">
      <div className="flex flex-col gap-[12px]">
        <div className="w-full flex justify-start items-center gap-[20px] flex-wrap">
          <div
            role="radio"
            aria-checked={opType === "AGGREGATION"}
            tabIndex={canChangeOpType ? 0 : -1}
            onClick={() => canChangeOpType && handleOpTypeChange("AGGREGATION")}
            onKeyDown={(e) => {
              if (canChangeOpType && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleOpTypeChange("AGGREGATION");
              }
            }}
            className={`flex gap-[10px] items-center ${
              canChangeOpType ? "cursor-pointer" : "cursor-not-allowed opacity-60"
            } ${opType === "AGGREGATION" ? "text-green" : "text-gray-700"}`}
          >
            <div
              className={`w-[18px] h-[18px] border rounded-full flex items-center justify-center ${
                opType === "AGGREGATION" ? "border-green" : "border-gray-300"
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  opType === "AGGREGATION" ? "bg-green" : "bg-gray-300"
                }`}
              />
            </div>
            <span>{t("document_aggregation.type_aggregation")}</span>
          </div>
          <div
            role="radio"
            aria-checked={opType === "DEAGGREGATION"}
            tabIndex={canChangeOpType ? 0 : -1}
            onClick={() => canChangeOpType && handleOpTypeChange("DEAGGREGATION")}
            onKeyDown={(e) => {
              if (canChangeOpType && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleOpTypeChange("DEAGGREGATION");
              }
            }}
            className={`flex gap-[10px] items-center ${
              canChangeOpType ? "cursor-pointer" : "cursor-not-allowed opacity-60"
            } ${opType === "DEAGGREGATION" ? "text-green" : "text-gray-700"}`}
          >
            <div
              className={`w-[18px] h-[18px] border rounded-full flex items-center justify-center ${
                opType === "DEAGGREGATION" ? "border-green" : "border-gray-300"
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  opType === "DEAGGREGATION" ? "bg-green" : "bg-gray-300"
                }`}
              />
            </div>
            <span>{t("document_aggregation.type_deaggregation")}</span>
          </div>
        </div>

        <div className="flex flex-row text-[20px] font-bold gap-[6px] text-gray-700 flex-wrap">
          <StatusBadge type={statusType?.type}>
            {t("documentLabels.status") + ": " + statusType?.label}
          </StatusBadge>
          <span>
            {t("documentLabels.create_document")}:{" "}
            {getDescription(AGGREGATION_DOC_TYPE, t)}
            {initialValues?.docNumber ? ` №${initialValues.docNumber}` : ""}
            {initialValues?.createdAt
              ? ` ${t("documentLabels.from")} ${format(new Date(initialValues.createdAt), "dd.MM.yyyy")}`
              : ""}
          </span>
        </div>

        <div className="flex flex-row flex-wrap justify-between items-center gap-[20px]">
          <div className="my-auto w-1/2 min-w-[240px] max-w-[50%]">
            <Stepper
              steps={[
                t("document_aggregation.step_products"),
                t("document_aggregation.step_sign"),
              ]}
              currentStep={status === "PROCESSED" ? 2 : 1}
            />
          </div>
          <div className="flex flex-row gap-[20px] flex-wrap shrink-0">
            {docId &&
            status === "PROCESSED" &&
            tableData?.totalItems &&
            tableData.totalItems > 0 ? (
              <button
                type="button"
                className="bg-green hover:brightness-75 text-white font-bold py-2 px-4 rounded"
                onClick={() => getDocument()}
              >
                {t("documentLabels_3.download_marks")}
              </button>
            ) : null}
            <button
              type="button"
              className="bg-bg hover:brightness-75 text-gray-500 font-bold py-2 px-4 rounded"
              onClick={() => navigate("/documents/drafts")}
            >
              {t("mainButtons.close")}
            </button>
            {canEdit && (
              <button
                type="button"
                className="bg-green hover:brightness-75 text-white font-bold py-2 px-4 rounded"
                onClick={handleSave}
              >
                {t("mainButtons.save")}
              </button>
            )}
            {status === "DRAFT" && canEdit && (
              <button
                type="button"
                className="bg-green hover:brightness-75 text-white font-bold py-2 px-4 rounded disabled:opacity-50 buttonEtrackc"
                disabled={
                  opType === "AGGREGATION"
                    ? !parentMark || !tableData?.totalItems
                    : !tableData?.totalItems
                }
                onClick={handleSign}
              >
                {t("mainButtons.sign")}
              </button>
            )}
          </div>
        </div>

        {opType === "AGGREGATION" ? (
          <>
            <div className="relative z-20 flex flex-col bg-white my-2 px-5 py-4 rounded-xl shadow-md gap-4">
              <div className="flex flex-wrap gap-4 items-center">
                {canEdit && (
                  <div className="z-[1000] relative">
                    <button
                      type="button"
                      className="bg-green text-white font-semibold py-2 px-4 rounded inline-flex items-center"
                      data-tooltip-offset={4}
                      data-tooltip-id="agg-parent-menu"
                    >
                      <span className="font-bold text-lg leading-none mr-2">+</span>
                      {t("document_aggregation.add_parent_code")}
                    </button>
                    <Tooltip
                      id="agg-parent-menu"
                      className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
                      openOnClick={true}
                      clickable={true}
                      noArrow={true}
                      opacity={1}
                      place="bottom-start"
                    >
                      <div
                        className={classNames(
                          style.tooltip,
                          "flex flex-col min-w-[208px] p-0 rounded-lg overflow-hidden",
                        )}
                      >
                        <button
                          type="button"
                          className="flex h-10 items-center px-4 duration-200 text-sm justify-between"
                          onClick={() => openParentPicker("list")}
                        >
                          <span>{t("documents_orders.add_codes")}</span>
                        </button>
                        <button
                          type="button"
                          className="flex h-10 items-center px-4 duration-200 text-sm justify-between"
                          onClick={() => openParentPicker("scan")}
                        >
                          <span>{t("mark_scan.scan_codes")}</span>
                        </button>
                      </div>
                    </Tooltip>
                  </div>
                )}
                <div className="flex flex-col gap-1 min-w-[280px]">
                  <label className="text-sm font-semibold text-gray-700">
                    {t("document_aggregation.parent_code")}
                  </label>
                  <div className={`${style.placeholder} flex items-center justify-between gap-3`}>
                    <span className="break-all">
                      {parentMark?.code || t("document_aggregation.parent_placeholder")}
                    </span>
                    {parentMark && canEdit && (
                      <button
                        type="button"
                        onClick={clearParent}
                        disabled={hasChildMarks}
                        title={
                          hasChildMarks
                            ? t("document_aggregation.parent_delete_blocked")
                            : t("mainButtons.delete")
                        }
                        className="shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <img src="/icons/trash.svg" alt="" className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="relative z-10 flex flex-col bg-white my-2 px-5 py-4 rounded-xl shadow-md gap-4">
              <div className="flex flex-wrap gap-4 items-center">
                {canEdit && (
                  <div className="z-[1000] relative">
                    <button
                      type="button"
                      className="bg-green text-white font-semibold py-2 px-4 rounded inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                      data-tooltip-offset={4}
                      data-tooltip-id={parentMark ? "agg-children-menu" : undefined}
                      disabled={!parentMark}
                    >
                      <span className="font-bold text-lg leading-none mr-2">+</span>
                      {t("document_aggregation.add_marks")}
                    </button>
                    <Tooltip
                      id="agg-children-menu"
                      className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
                      openOnClick={true}
                      clickable={true}
                      noArrow={true}
                      opacity={1}
                      place="bottom-start"
                    >
                      <div
                        className={classNames(
                          style.tooltip,
                          "flex flex-col min-w-[208px] p-0 rounded-lg overflow-hidden",
                        )}
                      >
                        <button
                          type="button"
                          className="flex h-10 items-center px-4 duration-200 text-sm justify-between"
                          onClick={() => setIsFileUploadModalOpen(true)}
                        >
                          <span>{t("documents_orders.upload_from_file")}</span>
                        </button>
                        <button
                          type="button"
                          className="flex h-10 items-center px-4 duration-200 text-sm justify-between"
                          onClick={() => setChildrenModalOpen(true)}
                        >
                          <span>{t("documents_orders.add_codes")}</span>
                        </button>
                        <button
                          type="button"
                          className="flex h-10 items-center px-4 duration-200 text-sm justify-between"
                          onClick={async () => {
                            const id = await ensureDocument();
                            if (!id || !parentMark?.id) return;
                            scanForRef.current = "children";
                            setScanFor("children");
                            setIsMarkScanModal(true);
                          }}
                        >
                          <span>{t("mark_scan.scan_codes")}</span>
                        </button>
                        <button
                          type="button"
                          className="flex h-10 items-center px-4 duration-200 text-sm justify-between"
                          onClick={() => setOrdersModalOpen(true)}
                        >
                          <span>{t("documents_orders.by_order_number")}</span>
                        </button>
                      </div>
                    </Tooltip>
                  </div>
                )}
                {canEdit && deleteMarks.length > 0 && (
                  <button
                    type="button"
                    className="bg-red-600 text-white font-semibold py-2 px-4 rounded"
                    onClick={() => setIsDeleteModalOpen(deleteMarks)}
                  >
                    {t("document_aggregation.delete_selected")}
                  </button>
                )}
              </div>

              {!parentMark ? (
                <EmptyListPlaceholderContent
                  title={t("document_aggregation.parent_required")}
                  description=""
                />
              ) : !tableData?.values?.length ? (
                <EmptyListPlaceholderContent
                  title={t("documents_orders.no_data_title")}
                  description={t("documents_orders.no_data_description")}
                />
              ) : (
                <div className="w-full">
                  <div className="rounded-lg overflow-x-auto bg-white">
                    <table className="max-w-full w-full leading-normal">
                      <thead className="border-b-2 border-gray-300">
                        <tr>
                          {canEdit && (
                            <th
                              className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                              scope="col"
                            />
                          )}
                          <th
                            className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                            scope="col"
                          >
                            №
                          </th>
                          <th
                            className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                            scope="col"
                          >
                            {t("CheckResult.code")}
                          </th>
                          <th
                            className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                            scope="col"
                          >
                            {t("document_aggregation.package_type")}
                          </th>
                          {canEdit && (
                            <th
                              className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                              scope="col"
                            />
                          )}
                        </tr>
                      </thead>
                      <tbody className="font-normal text-center">
                        {tableData.values.map((row: any, index: number) => {
                          const markId = row.markId ?? row.id;
                          return (
                            <tr key={markId || index}>
                              {canEdit && (
                                <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                                  <CheckBox
                                    id={`agg-row-${markId}`}
                                    checked={deleteMarks.includes(markId)}
                                    onChange={() =>
                                      setDeleteMarks((prev) =>
                                        prev.includes(markId)
                                          ? prev.filter((x) => x !== markId)
                                          : [...prev, markId],
                                      )
                                    }
                                  />
                                </td>
                              )}
                              <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                                {pagination.pageIndex * pagination.pageSize + index + 1}
                              </td>
                              <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600 break-all">
                                {(row.code || row.markCode || "").toString().slice(0, 21)}
                              </td>
                              <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600 whitespace-nowrap">
                                {formatKmTypeLabel(
                                  row.type ?? row.kmType ?? row.packagingType,
                                  t,
                                )}
                              </td>
                              {canEdit && (
                                <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                                  <button
                                    type="button"
                                    onClick={() => setIsDeleteModalOpen([markId])}
                                  >
                                    <img src="/icons/trash.svg" alt="" className="w-5 h-5 mx-auto" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {parentMark && tableData?.totalPages ? (
                <Pagination
                  totalItems={tableData?.totalItems || 0}
                  totalPages={tableData?.totalPages || 0}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              ) : null}
            </div>
          </>
        ) : (
          <div className="flex flex-col bg-white my-2 px-5 py-4 rounded-xl shadow-md gap-4">
            <div className="flex flex-wrap gap-4 items-center">
              {canEdit && (
                <div className="z-[1000] relative w-fit">
                  <button
                    type="button"
                    className="bg-green text-white font-semibold py-2 px-4 rounded w-fit inline-flex items-center"
                    data-tooltip-offset={4}
                    data-tooltip-id="agg-deagg-menu"
                  >
                    <span className="font-bold text-lg leading-none mr-2">+</span>
                    {t("document_aggregation.add_deaggregation_groups")}
                  </button>
                  <Tooltip
                    id="agg-deagg-menu"
                    className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
                    openOnClick={true}
                    clickable={true}
                    noArrow={true}
                    opacity={1}
                    place="bottom-start"
                  >
                    <div
                      className={classNames(
                        style.tooltip,
                        "flex flex-col min-w-[208px] p-0 rounded-lg overflow-hidden",
                      )}
                    >
                      <button
                        type="button"
                        className="flex h-10 items-center px-4 duration-200 text-sm justify-between"
                        onClick={() => openDeaggPicker("list")}
                      >
                        <span>{t("documents_orders.add_codes")}</span>
                      </button>
                      <button
                        type="button"
                        className="flex h-10 items-center px-4 duration-200 text-sm justify-between"
                        onClick={() => openDeaggPicker("scan")}
                      >
                        <span>{t("mark_scan.scan_codes")}</span>
                      </button>
                    </div>
                  </Tooltip>
                </div>
              )}
              {canEdit && deleteMarks.length > 0 && (
                <button
                  type="button"
                  className="bg-red-600 text-white font-semibold py-2 px-4 rounded"
                  onClick={() => setIsDeleteModalOpen(deleteMarks)}
                >
                  {t("document_aggregation.delete_selected")}
                </button>
              )}
            </div>
            {!tableData?.values?.length ? (
              <EmptyListPlaceholderContent
                title={t("documents_orders.no_data_title")}
                description={t("documents_orders.no_data_description")}
              />
            ) : (
              <div className="w-full">
                <div className="rounded-lg overflow-x-auto bg-white">
                  <table className="max-w-full w-full leading-normal">
                    <thead className="border-b-2 border-gray-300">
                      <tr>
                        {canEdit && (
                          <th
                            className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                            scope="col"
                          />
                        )}
                        <th
                          className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                          scope="col"
                        >
                          №
                        </th>
                        <th
                          className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                          scope="col"
                        >
                          {t("CheckResult.code")}
                        </th>
                        <th
                          className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                          scope="col"
                        >
                          {t("document_aggregation.order_number")}
                        </th>
                        <th
                          className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                          scope="col"
                        >
                          {t("document_aggregation.product_name")}
                        </th>
                        {canEdit && (
                          <th
                            className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                            scope="col"
                          />
                        )}
                      </tr>
                    </thead>
                    <tbody className="font-normal text-center">
                      {tableData.values.map((row: any, index: number) => {
                        const markId = row.markId ?? row.id;
                        return (
                          <tr key={`${markId ?? "m"}-${index}`}>
                            {canEdit && (
                              <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                                <CheckBox
                                  id={`deagg-row-${markId}-${index}`}
                                  checked={deleteMarks.includes(markId)}
                                  onChange={() =>
                                    setDeleteMarks((prev) =>
                                      prev.includes(markId)
                                        ? prev.filter((x) => x !== markId)
                                        : [...prev, markId],
                                    )
                                  }
                                />
                              </td>
                            )}
                            <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                              {pagination.pageIndex * pagination.pageSize + index + 1}
                            </td>
                            <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600 break-all">
                              {(row.code || row.markCode || "").toString()}
                            </td>
                            <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                              {row.orderNumber || "—"}
                            </td>
                            <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                              {row.productName || "—"}
                            </td>
                            {canEdit && (
                              <td className="px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600">
                                <button
                                  type="button"
                                  onClick={() => setIsDeleteModalOpen([markId])}
                                >
                                  <img
                                    src="/icons/trash.svg"
                                    alt=""
                                    className="w-5 h-5 mx-auto"
                                  />
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {tableData?.totalPages ? (
              <Pagination
                totalItems={tableData?.totalItems || 0}
                totalPages={tableData?.totalPages || 0}
                pagination={pagination}
                setPagination={setPagination}
              />
            ) : null}
          </div>
        )}
      </div>

      {parentModalOpen && (
        <DocumentsSearch
          isOpen={parentModalOpen}
          setIsOpen={setParentModalOpen}
          documentId={docId}
          toggleOnLoad={toggleOnLoad}
          setToggleOnLoad={setToggleOnLoad}
          aggregation={{
            mode: "parent",
            onSelectParent: (mark) => persistParent(mark),
          }}
          setErrorModal={setErrorModal}
        />
      )}
      {childrenModalOpen && parentMark?.id && (
        <DocumentsSearch
          isOpen={childrenModalOpen}
          setIsOpen={setChildrenModalOpen}
          documentId={docId}
          toggleOnLoad={toggleOnLoad}
          setToggleOnLoad={setToggleOnLoad}
          aggregation={{
            mode: "children",
            parentMarkId: parentMark.id,
          }}
          setErrorModal={setErrorModal}
        />
      )}

      <MarkScan
        isOpen={isMarkScanModal}
        setIsOpen={setIsMarkScanModal}
        documentId={docId}
        toggleOnLoad={toggleOnLoad}
        setToggleOnLoad={setToggleOnLoad}
        aggregationParentMarkId={
          (scanForRef.current === "children" || scanFor === "children")
            ? parentMark?.id
            : undefined
        }
        onCustomScanCodes={
          scanFor === "deaggregation" || scanForRef.current === "deaggregation"
            ? handleDeaggregationScanCodes
            : undefined
        }
      />

      {ordersModalOpen && parentMark?.id && (
        <AddByOrderNumber
          isOpen={ordersModalOpen}
          setIsOpen={setOrdersModalOpen}
          documentId={docId}
          toggleOnLoad={toggleOnLoad}
          setToggleOnLoad={setToggleOnLoad}
          aggregation={{ parentMarkId: parentMark.id }}
          setErrorModal={setErrorModal}
        />
      )}

      {parentMark?.id ? (
        <DocumentUpload
          isOpen={isFileUploadModalOpen}
          setIsOpen={setIsFileUploadModalOpen}
          documentId={docId}
          toggleOnLoad={toggleOnLoad}
          setToggleOnLoad={setToggleOnLoad}
          aggregation={{ parentMarkId: parentMark.id }}
        />
      ) : null}

      {deaggModalOpen && (
        <DocumentsSearch
          isOpen={deaggModalOpen}
          setIsOpen={setDeaggModalOpen}
          documentId={docId}
          toggleOnLoad={toggleOnLoad}
          setToggleOnLoad={setToggleOnLoad}
          aggregation={{ mode: "deaggregation" }}
          setErrorModal={setErrorModal}
        />
      )}

      <ModalComponent
        isOpen={!!isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(null)}
        headerContent={<div>{t("documentNotification.notification")}</div>}
        content={
          <p>
            {Array.isArray(isDeleteModalOpen) && isDeleteModalOpen.length === 1
              ? t("document_aggregation.delete_mark_confirm")
              : t("document_aggregation.delete_marks_confirm")}
          </p>
        }
        customContent={
          <div className="flex gap-3">
            <ModalButton color="green" type="button" onClick={handleDeleteMarks}>
              {t("mainButtons.delete")}
            </ModalButton>
            <ModalButton color="grey" type="button" onClick={() => setIsDeleteModalOpen(null)}>
              {t("mainButtons.close")}
            </ModalButton>
          </div>
        }
      />

      <ModalComponent
        isOpen={!!errorModal && !errorModal?.singleError && typeof errorModal !== "string"}
        onClose={() => setErrorModal(null)}
        headerContent={
          <h1 className="h__modal">
            {t("documentNotification.notification")} -{" "}
            {t("documentNotification.check_fields")}:
          </h1>
        }
        content={
          <div className="error-list">
            <ul>
              {Array.isArray(errorModal) && errorModal.length > 0 ? (
                errorModal.map((error: any, index: any) => (
                  <li className="border-b border-gray-300 py-1" key={index}>
                    {t(`error_dictionary.${error}`)}
                  </li>
                ))
              ) : (
                <li>{t("documentNotification.no_errors")}</li>
              )}
            </ul>
          </div>
        }
        customContent={
          <ModalButton type="button" onClick={() => setErrorModal(null)}>
            {t("mainButtons.accept")}
          </ModalButton>
        }
      />

      <ModalComponent
        isOpen={!!errorModal?.singleError || typeof errorModal === "string"}
        onClose={() => setErrorModal(null)}
        headerContent={
          <h1 className="h__modal">{t("documentNotification.notification")}</h1>
        }
        content={
          <div className="error-list">
            <p className="border-b border-gray-300 py-1">
              {t("documentStatus.error")}:{" "}
              {typeof errorModal === "string"
                ? errorModal
                : errorModal?.errorMessage}
            </p>
          </div>
        }
        customContent={
          <ModalButton type="button" onClick={() => setErrorModal(null)}>
            {t("mainButtons.accept")}
          </ModalButton>
        }
      />

      <CheckResult
        isOpen={isCheckOpen}
        setIsOpen={setIsCheckOpen}
        problemMarks={isCheckOpen}
        docId={docId}
      />

      <AlertBox isOpen={alertOpen} setIsOpen={setAlertOpen} />
      {loading ? <Loader /> : null}
    </div>
  );
}

export default DocumentAggregationOrders;
